import { Redirect, useHistory } from "react-router-dom";
import { MUNICIPALITY_STORAGE_KEY, URL } from "myconstants";
import { ReactElement, useEffect, useState } from "react";
import { Action, State } from "../../state/state";
import { Button } from "components/common/Button";
import { Select } from "components/common/Select";
import { Metrics } from "calculation/metric";
import { Parameters } from "calculation/parameter";
import { MetricDto, MunicipalityDto, ParameterDto } from "dtos/dtos";
import { Overlay } from "components/common/Overlay";

export function SelectMunicipality(props: {
  dispatch: (action: Action) => void;
  state: State;
}): ReactElement {
  const dispatch = props.dispatch;
  const state = props.state;
  const history = useHistory();
  const [error, setError] = useState<boolean>();
  const [loading, setLoading] = useState<boolean>(true);

  function onMunicipalityChange(bfsNr: string) {
    const selectedMunicipality = state.user.accessibleMunicipalities?.find(
      (m) => m.bfsNr.toString() === bfsNr
    );

    if (selectedMunicipality !== undefined) {
      dispatch({
        type: "setSelectedMunicipality",
        data: { municipality: selectedMunicipality },
      });

      window.sessionStorage.setItem(
        MUNICIPALITY_STORAGE_KEY,
        selectedMunicipality.bfsNr.toString()
      );
    }
  }

  useEffect(() => {
    async function loadMetaData() {
      if (
        state.metrics === undefined ||
        state.parameters === undefined ||
        state.municipalities === undefined
      ) {
        setLoading(true);

        try {
          const metricsResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/metrics`,
            {
              credentials: "include",
            }
          );

          if (metricsResponse.ok) {
            const metrics: MetricDto[] = await metricsResponse.json();

            const parametersResponse = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/parameters`,
              {
                credentials: "include",
              }
            );

            if (parametersResponse.ok) {
              const parameters: ParameterDto[] =
                await parametersResponse.json();

              const municipalitiesResponse = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/municipalities`,
                {
                  credentials: "include",
                }
              );

              if (municipalitiesResponse.ok) {
                const municipalities: MunicipalityDto[] =
                  await municipalitiesResponse.json();

                dispatch({
                  type: "setMetadata",
                  data: {
                    metrics: new Metrics(metrics),
                    parameters: new Parameters(parameters),
                    municipalities: municipalities,
                  },
                });
              } else {
                history.replace(URL.error);
              }
            } else {
              history.replace(URL.error);
            }
          } else {
            history.replace(URL.error);
          }
        } catch (error) {
          history.replace(URL.error);
        } finally {
          setLoading(false);
        }
      }
    }

    loadMetaData();
  }, [
    history,
    state.metrics,
    state.parameters,
    state.municipalities,
    dispatch,
  ]);

  useEffect(() => {
    async function loadAccessibleMunicipalities() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/accessible-municipalities`,
          {
            credentials: "include",
          }
        );
        if (response.ok) {
          const accessibleMunicipalities: MunicipalityDto[] =
            await response.json();
          dispatch({
            type: "setAccessibleMunicipalities",
            data: { municipalities: accessibleMunicipalities },
          });
        } else {
          setError(true);
        }
      } catch (error) {
        setError(true);
      }
    }

    loadAccessibleMunicipalities();
  }, [dispatch]);

  useEffect(() => {
    const bfsNrString = window.sessionStorage.getItem(MUNICIPALITY_STORAGE_KEY);

    if (
      bfsNrString !== null &&
      state.user.accessibleMunicipalities !== undefined &&
      state.selectedMunicipality === undefined
    ) {
      const selectedMunicipality = state.user.accessibleMunicipalities?.find(
        (m) => m.bfsNr.toString() === bfsNrString
      );

      dispatch({
        type: "setSelectedMunicipality",
        data: { municipality: selectedMunicipality },
      });

      history.push(URL.zoningPlanLegalCalculation);
    }
  }, [
    state.selectedMunicipality,
    state.user.accessibleMunicipalities,
    dispatch,
    history,
  ]);

  if (error) {
    return <Redirect to={URL.error} />;
  }

  return (
    <main className="flex flex-col z-20 py-8 w-3/4 max-w-[800px] mx-auto h-full">
      <Overlay show={loading} text="Lade Daten ..." />
      <p className="mb-4 text-gray-700">
        Bitte wählen Sie die zu analysierende Gemeinde aus. Sie können Ihre Wahl
        mittels Auswahlfeld oben rechts jederzeit ändern (in Abhängigkeit Ihrer
        Berechtigungen).
      </p>
      <form>
        <fieldset className="flex flex-col space-y-4 border-2 border-gray-200 rounded-md px-4 py-2 w-80">
          <legend className="text-blue-400 font-bold">
            Gemeinde auswählen
          </legend>
          <Select
            header={{ displayValue: "Gemeinde auswählen", enabled: false }}
            options={state.user.accessibleMunicipalities?.map((m) => {
              return { value: m.bfsNr.toString(), displayValue: m.gemeinde };
            })}
            value={state.selectedMunicipality?.bfsNr.toString()}
            width="w-auto"
            visible={true}
            onChange={(value) => onMunicipalityChange(value)}
          />
          <Button
            className="bg-blue-400"
            onClick={() => {
              if (state.selectedMunicipality !== undefined) {
                history.push(URL.zoningPlanLegalCalculation);
              }
            }}
          >
            Weiter
          </Button>
        </fieldset>
      </form>
    </main>
  );
}
